import "./InboundFlow.css";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from "dompurify";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import { Menu, MenuItem } from "@mui/material";
import { Modal } from "react-bootstrap";
import moment from "moment";
import { gridFilteredSortedRowIdsSelector } from "@mui/x-data-grid/hooks/features/filter";
import { gridRowTreeSelector } from "@mui/x-data-grid/hooks/features/rows";
import { useContextStateManagement } from "../../context/ContextApi";
import { useDispatch } from "react-redux";
import { logout } from "../../features/userSlice";
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  List,
  Filter,
  MoreVertical,
} from "lucide-react";
import CallsBreakdownDialog from "../CallDialogs/CallsBreakdownDialog";
import CallFilterDialog from "../CallDialogs/CallFilterDialog";
import {
  enrichInboundCallRows,
  getLatestInboundCallDate,
  buildLatestCallDateInRangeMap,
  normalizeTransferNumber,
  safeDate,
  maskPhone,
} from "../../inboundCallHelpers";
import {
  DEFAULT_PRACTICE_TIMEZONE,
  formatInPracticeTimezone,
  startOfDayInPracticeTimezone,
  endOfDayInPracticeTimezone,
} from "../../practiceTimezone";
import { PageActionMenu } from "../../components/PageActionMenu/PageActionMenu";

// Used by the "View Transfer" modal - was called without ever being defined, which threw
// on every render of that modal and (with no error boundary anywhere in this app) took the
// whole page down blank instead of just failing to show the duration.
const formatTransferDuration = (sec) => {
  const totalSeconds = Math.max(0, Number(sec) || 0);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const CSV_EXPORT_HEADERS = [
  "Status",
  "Date",
  "RootCallSid",
  "ClientNumber",
  "OrganizationNumber",
  "Transfer",
  "FollowUpReason",
];
const INBOUND_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const INBOUND_FILTERS_STORAGE_KEY = "cm_inboundFilters";
const INBOUND_FILTER_DEFAULTS = {
  range: "all",
  transfer: "all",
  duration: "all",
  followUp: "all",
  customStart: "",
  customEnd: "",
};
const INBOUND_DURATION_BUCKETS = {
  short: { max: 120 },
  medium: { min: 120, max: 300 },
  long: { min: 300 },
};
const INBOUND_FILTER_SECTIONS = [
  {
    title: "Range",
    fields: [
      {
        name: "range",
        label: "Range",
        type: "select",
        options: [
          { value: "all", label: "All" },
          { value: "24h", label: "24h" },
          { value: "7d", label: "7d" },
          { value: "30d", label: "30d" },
          { value: "custom", label: "Custom" },
        ],
      },
      {
        name: "customStart",
        label: "Custom Start",
        type: "date",
        showWhen: (values) => values.range === "custom",
      },
      {
        name: "customEnd",
        label: "Custom End",
        type: "date",
        showWhen: (values) => values.range === "custom",
      },
    ],
  },
  {
    title: "Inbound Filters",
    fields: [
      {
        name: "transfer",
        label: "Transfer",
        type: "select",
        options: [
          { value: "all", label: "All" },
          { value: "transferred", label: "Transferred" },
          { value: "not_transferred", label: "Not Transferred" },
        ],
      },
      {
        name: "duration",
        label: "Duration",
        type: "select",
        options: [
          { value: "all", label: "All" },
          { value: "short", label: "Short <2 min" },
          { value: "medium", label: "Medium 2-5 min" },
          { value: "long", label: "Long >5 min" },
        ],
      },
      {
        name: "followUp",
        label: "Follow-up",
        type: "select",
        options: [
          { value: "all", label: "All" },
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" },
        ],
      },
    ],
  },
];

const toDate = (value) => {
  const parsed = value ? new Date(value) : null;
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
};

// getLatestInboundCallDate, not just row.CreateDateTime - a lead's CreateDateTime is frozen at
// first-ever contact and never advances for returning callers, so the Range filter's 24h/7d/30d
// presets (and its custom start/end bounds) were checking "when was this lead's record first
// created" instead of "does this lead have recent call activity" - almost never true for leads
// uploaded well in the past, which is why picking any preset showed 0 rows.
const getInboundRowDate = (row) => getLatestInboundCallDate(row) || toDate(row?.date || null);

// Falls through to the next candidate field whenever the current one is exactly 0, not just
// when it's null/undefined - a `??` chain alone would stop at the first field that's merely
// *present*, so a lead whose real talk time only got recorded onto TotalAllLegsDurationSec
// (e.g. answer time living on a child SIP leg rather than the root leg) would still read as
// 0 if TotalDurationSec happened to come back 0 for that lead, hiding real duration data and
// making the Duration filter's Short/Medium buckets look permanently empty.
const getInboundDurationSec = (row) => {
  const candidates = [
    row?.TotalDurationSec,
    row?.TotalAllLegsDurationSec,
    row?.DurationSec,
    row?.durationSec,
  ];
  const firstNonZero = candidates.find((value) => Number(value) > 0);
  return Number(firstNonZero ?? 0) || 0;
};

const rowMatchesDuration = (durationSec, durationFilter) => {
  if (durationFilter === "all") return true;
  if (durationFilter === "short") return durationSec < INBOUND_DURATION_BUCKETS.short.max;
  if (durationFilter === "medium") {
    return (
      durationSec >= INBOUND_DURATION_BUCKETS.medium.min &&
      durationSec <= INBOUND_DURATION_BUCKETS.medium.max
    );
  }
  if (durationFilter === "long") return durationSec > INBOUND_DURATION_BUCKETS.long.min;
  return true;
};

const isInboundTransferred = (row) => {
  const transferValue = String(row?.TransferNumber || row?.transferNumber || "").trim();
  return Boolean(transferValue && transferValue !== "-");
};

const isCallTransferred = (call) => {
  const value = String(call?.TransferNumber ?? call?.transferNumber ?? "").trim();
  return Boolean(value && value !== "-");
};

// Whether at least one of this lead's real call attempts matches the requested transfer state.
// Checking only row.TransferNumber (the lead's own aggregate field) meant a lead with even one
// transferred call among many non-transferred ones was hidden ENTIRELY by "Not Transferred" -
// including its many genuinely non-transferred calls. Falls back to the aggregate field only
// when no Calls[] array is available to check call-by-call.
const inboundRowMatchesTransferFilter = (row, transferFilter) => {
  const calls = Array.isArray(row?.Calls) ? row.Calls : null;
  if (!calls || calls.length === 0) {
    const rowTransferred = isInboundTransferred(row);
    return transferFilter === "transferred" ? rowTransferred : !rowTransferred;
  }
  return transferFilter === "transferred"
    ? calls.some(isCallTransferred)
    : calls.some((call) => !isCallTransferred(call));
};

const isInboundFollowUp = (row) => {
  const value = extractFollowUpReason(row?.FollowUpReason || row?.followUpReason || "");
  return Boolean(value);
};

// "Custom" reads its start/end from plain YYYY-MM-DD date inputs - startOfDayInPracticeTimezone/
// endOfDayInPracticeTimezone resolve those against the practice's own timezone (matching how
// activeDateFilter/the rest of this file already reason about calendar days). Using
// toDate/new Date(dateKey) here instead (like this used to) parses the string as UTC midnight
// regardless of timeZone, which could exclude most of the selected end day for a UTC-behind
// practice, or include the day before the selected start day for a UTC-ahead one.
const getRangeStart = (rangeKey, customStart, timeZone) => {
  if (rangeKey === "24h") return new Date(Date.now() - 24 * 60 * 60 * 1000);
  if (rangeKey === "7d") return new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  if (rangeKey === "30d") return new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  if (rangeKey === "custom") return startOfDayInPracticeTimezone(customStart, timeZone);
  return null;
};

const twilioBillableMinutes = (calls) => {
  if (!Array.isArray(calls)) return 0;
  return calls.reduce((sum, c) => {
    const legs = Array.isArray(c?.legs) ? c.legs : [];
    if (legs.length === 0) {
      const sec = Number(c?.billableSec) || Number(c?.durationSec) || 0;
      return sum + Math.max(0, Math.ceil(sec / 60));
    }
    return (
      sum +
      legs.reduce(
        (s, l) => s + Math.max(0, Math.ceil((Number(l?.durationSec) || 0) / 60)),
        0,
      )
    );
  }, 0);
};


const normalizeCsvText = (value) =>
  String(value ?? "")
    .replace(/\r?\n+/g, " | ")
    .replace(/\s+/g, " ")
    .trim();

const csvEscape = (value) => {
  const normalized = normalizeCsvText(value);

  if (/[",\n]/.test(normalized)) {
    return `"${normalized.replace(/"/g, '""')}"`;
  }

  return normalized;
};

// Excel auto-detects a date/time-looking string in a CSV and silently re-parses it into its own
// date serial number, which then renders as "#####" once the column isn't wide enough to show it
// (a plain text string never has this problem - only something Excel recognizes as a real date).
// ="..." forces Excel to keep it as literal text instead, no matter the column width.
const csvForceText = (value) => `="${normalizeCsvText(value).replace(/"/g, '""')}"`;

const formatCsvPhoneLikeValue = (value) => {
  const normalized = normalizeCsvText(value);

  if (!normalized || /^not provided$/i.test(normalized) || normalized === "-") {
    return normalized || "Not provided";
  }

  const digits = normalized.replace(/\D/g, "");

  if (digits.length === 11 && digits.startsWith("1")) {
    return `${digits[0]}-${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  return normalized;
};

const FOLLOW_UP_NEGATIVE_PATTERNS = [
  /^[-–—]+$/i,
  /^not provided$/i,
  /^none$/i,
  /^no$/i,
  /^n\/a$/i,
  /^na$/i,
  /^not required$/i,
  /^true$/i,
  /^false$/i,
  /^follow[- ]?up\s*:\s*(none|no|n\/a|na|not required)\.?$/i,
  /^no follow[- ]?up needed\.?$/i,
];

const isNegativeFollowUpReason = (value) => {
  const normalized = normalizeCsvText(value).replace(/[.]+$/g, "");

  if (!normalized) {
    return true;
  }

  return FOLLOW_UP_NEGATIVE_PATTERNS.some((pattern) => pattern.test(normalized));
};

const normalizeExtractedFollowUpReason = (value) => {
  const normalized = normalizeCsvText(value)
    .replace(/^[:\-|,\s]+/, "")
    .replace(/[|,\s]+$/, "");

  return normalized || null;
};

const extractFollowUpReason = (value) => {
  const normalized = normalizeCsvText(value);

  if (!normalized || isNegativeFollowUpReason(normalized)) {
    return null;
  }

  const explicitMatch = normalized.match(
    /follow[- ]?up\s*:\s*([\s\S]*?)(?=(?:\s+[A-Z][A-Za-z][A-Za-z -]*\s*:)|(?:\s*\|\s*)|$)/i,
  );
  const explicitReason = normalizeExtractedFollowUpReason(explicitMatch?.[1]);

  if (explicitMatch) {
    return explicitReason && !isNegativeFollowUpReason(explicitReason)
      ? explicitReason
      : null;
  }

  if (/^(transfer|outcome|flags|assistant)\s*:/i.test(normalized)) {
    return null;
  }

  return normalized;
};

const formatFollowUpReasonForCsv = (value) => extractFollowUpReason(value) || "-";

const getHistoryEntryDate = (entry, row) =>
  entry?.date ||
  entry?.CreateDateTime ||
  entry?.startedAt ||
  row?.CreateDateTime ||
  "";

const resolveHistorySummary = (entry, row, isSingleAttempt) =>
  entry?.summary ||
  entry?.Summary ||
  (isSingleAttempt ? row?.CallSummary : "") ||
  "-";

const resolveHistoryTranscript = (entry, row, isSingleAttempt) =>
  entry?.transcript ||
  entry?.TranscriptText ||
  entry?.Transcript ||
  (isSingleAttempt ? row?.Transcript : "") ||
  "-";

const resolveHistoryTransferNumber = (entry, row, isSingleAttempt) =>
  normalizeTransferNumber(
    entry?.TransferNumber ??
      entry?.transferNumber ??
      (isSingleAttempt ? row?.TransferNumber : undefined),
  );

const resolveHistoryFollowUpReason = (entry, row, isSingleAttempt) => {
  const value =
    entry?.FollowUpReason ??
    entry?.followUpReason ??
    (isSingleAttempt ? row?.FollowUpReason : undefined);

  return extractFollowUpReason(value);
};

// Converts to the configured practice/user timezone before writing to the CSV - this used to
// write the raw value as-is (effectively UTC), unlike Outbound's and Summary's CSV exports
// which already convert via formatInPracticeTimezone.
const formatExportDate = (value, timeZone) =>
  formatInPracticeTimezone(value, timeZone) || "Not provided";

const buildHistoryEntriesForExport = (row) => {
  const historyEntries = row?.Calls || row?.CallHistory || row?.History;

  if (!Array.isArray(historyEntries) || historyEntries.length === 0) {
    return [row];
  }

  return [...historyEntries].sort(
    (left, right) =>
      new Date(getHistoryEntryDate(left, row)).getTime() -
      new Date(getHistoryEntryDate(right, row)).getTime(),
  );
};

// Both checks below apply to ONE call at a time, not the whole lead. A lead can qualify for the
// date range / transfer filter (applyInboundRowFilters, or the backend's own date-range lead
// lookup) because of ONE call, while row.Calls[] still carries every other call that lead ever
// made. Without these, the CSV listed all of a qualifying lead's calls, including calls outside
// the picked date range (Issue 3) and calls that themselves never transferred (Issue 9).
const buildMultiRowCsv = (rows, exportFilters = {}) => {
  const { dateRange, transferFilter, timeZone } = exportFilters;
  const csvLines = [CSV_EXPORT_HEADERS.join(",")];

  rows.forEach((row) => {
    const historyEntries = buildHistoryEntriesForExport(row);
    const isSingleAttempt = historyEntries.length === 1;

    historyEntries.forEach((entry) => {
      if (dateRange) {
        const entryDate = safeDate(getHistoryEntryDate(entry, row));
        if (!entryDate) return;
        const dayKey = dateRange.dayFormatter.format(entryDate);
        if (dayKey < dateRange.startDate || dayKey > dateRange.endDate) return;
      }

      const transferNumber = resolveHistoryTransferNumber(entry, row, isSingleAttempt);

      if (transferFilter && transferFilter !== "all") {
        const wasTransferred = isInboundTransferred({ TransferNumber: transferNumber });
        if (transferFilter === "transferred" && !wasTransferred) return;
        if (transferFilter === "not_transferred" && wasTransferred) return;
      }

      const followUpReason = resolveHistoryFollowUpReason(entry, row, isSingleAttempt);
      csvLines.push(
        [
          csvEscape(entry?.status || entry?.Status || row?.Status || "Not provided"),
          csvForceText(formatExportDate(getHistoryEntryDate(entry, row), timeZone)),
          csvEscape(
            entry?.rootCallSid ||
              entry?.RootCallSid ||
              entry?.RootCallSID ||
              row?.rootCallSid ||
              row?.RootCallSid ||
              row?.RootCallSID ||
              "Not provided",
          ),
          csvEscape(maskPhone(row?.PatientContactNumber)),
          csvEscape(
            formatCsvPhoneLikeValue(
              row?.ProviderContactNumber ||
                row?.ClinicNumber ||
                row?.ProviderFacilityName ||
                "Not provided",
            ),
          ),
          csvEscape(formatCsvPhoneLikeValue(transferNumber)),
          csvEscape(formatFollowUpReasonForCsv(followUpReason)),
        ].join(","),
      );
    });
  });

  return csvLines.join("\r\n");
};

const downloadCsvFile = (content, fileName) => {
  const blob = new Blob(["\uFEFF", content], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export default function InBoundCalls({
  setIsCallsDataGet,
  setIsUploadLead,
  inBoundsCalls,
  setInBoundsCalls,
  providerName,
  patientName,
  startDate,
  endDate,
  isUploadLead,
  tableTopBarControls = null,
  filterNonce,
}) {

  const [modalShow, setModalShow] = useState(false)
  const [activeModal, setActiveModal] = useState();
  const [userId, setUserId] = useState();
  const [menuId, setMenuId] = useState(null);
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [transcriptRecord, setTranscriptRecord] = useState("");
  const [summary, setSummary] = useState("");
  const [status, setStatus] = useState("");
  const [transferHistory, setTransferHistory] = useState([]);
  const [followUpReasonHistory, setFollowUpReasonHistory] = useState([]);
  const [isData, setIsData] = useState(false);
  const [callsDialogRow, setCallsDialogRow] = useState(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuRow, setActionMenuRow] = useState(null);
  // True server-side pagination (matches Summary tab exactly) - each page change fetches
  // exactly that page directly from the backend and replaces inBoundsCalls outright. There is
  // no "loaded so far vs. true total" staging at all, so there's nothing that can read as stuck.
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [totalRows, setTotalRows] = useState(0);
  // Bumped to force a re-fetch of the current page even when paginationModel/activeDateFilter
  // haven't actually changed (e.g. after an action that changes the underlying list).
  const [refetchNonce, setRefetchNonce] = useState(0);
  const inboundFetchSeqRef = useRef(0);
  // The practice's own configured timezone, resolved server-side alongside the date-filtered
  // rows above - rendering their timestamps converted to this same zone (instead of the
  // browser's local zone) is what keeps "what day" a call falls on agreeing with the filter.
  const [practiceTimezone, setPracticeTimezone] = useState(DEFAULT_PRACTICE_TIMEZONE);
  // The practice's own configured zone (server-resolved, real even when GeneralCallSettings has
  // no row at all for this practice) - distinct from practiceTimezone above, which is the
  // viewer's own effective zone. Passed to CallsBreakdownDialog as fixedTimezone below.
  const [fixedTimezone, setFixedTimezone] = useState(DEFAULT_PRACTICE_TIMEZONE);
  // Which date range (if any) the current page/Load-more sequence is
  // scoped to - null means the default, unfiltered PriorAuthQLead view. Seeded from the
  // startDate/endDate props at mount (now always populated with a month-to-date default - see
  // useConsoleSearch) instead of hardcoding null, so the very first fetch already hits the
  // date-bounded, correctly-paginated SearchWithDatePAuthQ endpoint instead of the unbounded one.
  const [activeDateFilter, setActiveDateFilter] = useState(() =>
    startDate && endDate ? { startDate, endDate } : null,
  );
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState(() => {
    try {
      const stored = sessionStorage.getItem(INBOUND_FILTERS_STORAGE_KEY);
      return stored ? { ...INBOUND_FILTER_DEFAULTS, ...JSON.parse(stored) } : INBOUND_FILTER_DEFAULTS;
    } catch (_) {
      return INBOUND_FILTER_DEFAULTS;
    }
  });

  // Remember the Inbound table's filters across tab switches and refreshes, mirroring the
  // sessionStorage pattern ContextApi.js already uses for sideBarLinkActive/activeTab.
  useEffect(() => {
    sessionStorage.setItem(INBOUND_FILTERS_STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { PracticeId, setActiveTab, token, usersPostRequest, setSideBarLinkActive, setToken, dateRangeRequest, paginatedRequest, userType, setUserType, practiceContactNumber, inboundContact, timezonePreferenceVersion } = useContextStateManagement();

  // Pulled out of the useMemo below so the same client-side filtering (range/transfer/
  // duration/follow-up - everything /api/PriorAuthQLead and /api/SearchWithDatePAuthQ themselves
  // can't filter on) can also be applied to a freshly, fully fetched row set for "Download All",
  // instead of only ever running against whatever page(s) happen to already be loaded.
  const applyInboundRowFilters = (rows, activeFilters) => {
    const rangeStart = getRangeStart(activeFilters.range, activeFilters.customStart, practiceTimezone);
    const rangeEnd =
      activeFilters.range === "custom"
        ? endOfDayInPracticeTimezone(activeFilters.customEnd, practiceTimezone)
        : new Date();

    return (Array.isArray(rows) ? rows : []).filter((row) => {
      const rowDate = getInboundRowDate(row);
      const durationSec = getInboundDurationSec(row);

      if (rangeStart && (!rowDate || rowDate < rangeStart)) {
        return false;
      }

      if (rangeEnd && activeFilters.range === "custom" && rowDate && rowDate > rangeEnd) {
        return false;
      }

      if (activeFilters.transfer === "transferred" && !inboundRowMatchesTransferFilter(row, "transferred")) {
        return false;
      }

      if (activeFilters.transfer === "not_transferred" && !inboundRowMatchesTransferFilter(row, "not_transferred")) {
        return false;
      }

      if (!rowMatchesDuration(durationSec, activeFilters.duration)) {
        return false;
      }

      if (activeFilters.followUp === "yes" && !isInboundFollowUp(row)) {
        return false;
      }

      if (activeFilters.followUp === "no" && isInboundFollowUp(row)) {
        return false;
      }

      return true;
    });
  };

  // True once any filter beyond the server-side date range is engaged (range preset/custom,
  // transfer, or follow-up, or duration). None of these have a server-side equivalent, so the
  // true "how many rows actually match" figure can only be known by fetching every row that
  // matches the date filter and counting the client-side-filtered result - unlike the
  // unfiltered case, one raw server page can't tell you that on its own.
  const hasActiveClientFilter =
    filters.range !== "all" ||
    filters.transfer !== "all" ||
    filters.duration !== "all" ||
    filters.followUp !== "all";

  const filteredInboundRows = useMemo(() => {
    const allFiltered = applyInboundRowFilters(inBoundsCalls, filters);
    if (!hasActiveClientFilter) {
      // inBoundsCalls already holds exactly this page's rows (true server-side pagination) -
      // the filter pass above is a no-op here, so this already IS the current page.
      return allFiltered;
    }
    // inBoundsCalls holds every row matching the date filter in this mode (fetched all at
    // once) - slice out just the current page after filtering, so pageCount/footer math
    // (computed from totalRows, kept in sync below) lines up with what's actually rendered.
    const offset = paginationModel.page * paginationModel.pageSize;
    return allFiltered.slice(offset, offset + paginationModel.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, inBoundsCalls, hasActiveClientFilter, paginationModel.page, paginationModel.pageSize]);

  // Shared by View Transfer/View Reason below - View Status/Transcript/Summary
  // (transcriptionsFetchHandle) already scope to activeDateFilter this same way; these two
  // never did, so they kept showing a lead's entire history regardless of the selected range.
  // One formatter built per modal-open call, not per record - same reasoning as
  // buildLatestCallDateInRangeMap (a row can carry dozens of call attempts).
  const buildDateRangeChecker = () => {
    if (!activeDateFilter) return () => true;
    const dayFormatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: practiceTimezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    return (dateValue) => {
      const parsed = safeDate(dateValue);
      if (!parsed) return false;
      const dayKey = dayFormatter.format(parsed);
      return dayKey >= activeDateFilter.startDate && dayKey <= activeDateFilter.endDate;
    };
  };

  const buildTransferHistory = (row) => {
    const calls = Array.isArray(row?.Calls) ? row.Calls : [];
    const isInRange = buildDateRangeChecker();

    return calls
      .filter((call) => isInRange(call?.startedAt || call?.CreateDateTime))
      .flatMap((call) =>
        (Array.isArray(call?.legs) ? call.legs : [])
          .filter((leg) => leg?.isTransferLeg || leg?.role === "transfer")
          .map((leg) => ({
            fromNumber: leg?.fromNumber || leg?.from || "",
            transferredNumber: leg?.toNumber || leg?.to || row?.TransferNumber || "-",
            startedAt: leg?.startTime || leg?.startedAt || call?.startedAt || null,
            endedAt: leg?.endTime || leg?.endedAt || null,
            durationSec: Number(leg?.duration || leg?.durationSec || 0),
          })),
      )
      .sort(
        (left, right) =>
          new Date(right?.startedAt || 0).getTime() -
          new Date(left?.startedAt || 0).getTime(),
      );
  };

  const buildFollowUpReasonHistory = (row) => {
    const calls = Array.isArray(row?.Calls) ? row.Calls : [];
    const isInRange = buildDateRangeChecker();
    const followUpEntries = calls
      .filter((call) => isInRange(call?.startedAt || call?.CreateDateTime))
      .map((call) => ({
        reason: extractFollowUpReason(
          call?.FollowUpReason || call?.followUpReason || "",
        ),
        startedAt: call?.startedAt || call?.CreateDateTime || null,
      }))
      .filter((entry) => entry.reason);

    if (followUpEntries.length > 0) {
      return followUpEntries.sort(
        (left, right) =>
          new Date(right?.startedAt || 0).getTime() -
          new Date(left?.startedAt || 0).getTime(),
      );
    }

    // Fall back to the lead's own aggregate FollowUpReason only when its own CreateDateTime is
    // itself in range - otherwise a stale reason from outside the selected range would leak in
    // just because no per-call reason happened to match.
    if (isInRange(row?.CreateDateTime)) {
      const rowFollowUpReason = extractFollowUpReason(row?.FollowUpReason || "");

      if (rowFollowUpReason) {
        return [
          {
            reason: rowFollowUpReason,
            startedAt: row?.CreateDateTime || null,
          },
        ];
      }
    }

    return [];
  };

  const openTransferModal = (row) => {
    setTransferHistory(buildTransferHistory(row));
    setActiveModal("transfer");
    setModalShow(true);
  };

  const openFollowUpReasonModal = (row) => {
    setFollowUpReasonHistory(buildFollowUpReasonHistory(row));
    setActiveModal("followUpReason");
    setModalShow(true);
  };

  const actionMenuOpen = Boolean(actionMenuAnchor);

  const handleOpenActionMenu = (event, row) => {
    event.stopPropagation();
    setActionMenuAnchor(event.currentTarget);
    setActionMenuRow(row);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuRow(null);
  };

  // While a calendar date range is selected, "latest call" should mean the latest call INSIDE
  // that range, not the latest one ever - a patient's row already only appears when they have at
  // least one call in range, so showing an all-time date here (possibly from outside the range
  // entirely) made the row look inconsistent with the filter that surfaced it. Computed once for
  // the whole page (not per cell) - see buildLatestCallDateInRangeMap's own comment for why.
  const latestCallDateInRangeById = useMemo(
    () =>
      activeDateFilter
        ? buildLatestCallDateInRangeMap(
            inBoundsCalls,
            activeDateFilter.startDate,
            activeDateFilter.endDate,
            practiceTimezone,
          )
        : new Map(),
    [inBoundsCalls, activeDateFilter, practiceTimezone],
  );

  const resolveLatestCallDate = (row) =>
    activeDateFilter
      ? latestCallDateInRangeById.get(row?.Id) || null
      : getLatestInboundCallDate(row);

  // The Call Details popup used to always show a lead's ENTIRE call history regardless of the
  // active date filter - fixed the same way as the Date & Time column above.
  const callsDialogFilteredCalls = useMemo(() => {
    const allCalls = Array.isArray(callsDialogRow?.Calls) ? callsDialogRow.Calls : [];
    if (!activeDateFilter) return allCalls;
    const dayFormatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: practiceTimezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    return allCalls.filter((call) => {
      const entryDate = safeDate(call?.startedAt || call?.CreateDateTime);
      if (!entryDate) return false;
      const dayKey = dayFormatter.format(entryDate);
      return dayKey >= activeDateFilter.startDate && dayKey <= activeDateFilter.endDate;
    });
  }, [callsDialogRow, activeDateFilter, practiceTimezone]);

  const columns = [
    {
      field: "Status",
      headerName: "Status",
      minWidth: 110,
      flex: 0.8,
      sortable: false,
      renderCell: (params) => (
        <button
          type="button"
          className="inbound-table__text-button"
          onClick={() => statusHandle(params.row.Id)}
        >
          View Status
        </button>
      ),
    },
    {
      field: "CreateDateTime",
      headerName: "Date & Time",
      minWidth: 188,
      flex: 1.2,
      // MUI's default sortingOrder is ['asc', 'desc', null] - a third click removes sorting
      // entirely and falls back to the natural (unsorted, date-unrelated) row order, which
      // reads as "random" for a date column. Restrict this column to just the two useful
      // states so clicking always toggles between ascending and descending.
      sortingOrder: ["asc", "desc"],
      // CreateDateTime is frozen at first-ever contact and never advances for
      // returning callers - show the most recent real call attempt instead.
      // valueGetter must return the SAME value renderCell displays below - otherwise the
      // column sorts by raw CreateDateTime while showing a different date, and clicking the
      // header to sort doesn't visibly put the displayed dates in order.
      //
      // Must also ALWAYS return the same type (a Date object, or null) for every row - this
      // column has no explicit `type`, so it defaults to MUI's 'string' column type, whose
      // sort comparator branches per-value: typeof value === 'string' does a locale string
      // compare, anything else does `value1 - value2`. getLatestInboundCallDate(row) returns a
      // Date object when it succeeds, but previously fell back to `value` (the raw
      // CreateDateTime string) when it returned null - mixing Date objects and strings across
      // rows. Comparing a Date against a string coerces the string with Number(), which is NaN
      // for an ISO date string, and an invalid (NaN) comparator result is precisely what
      // produces "random"-looking order once real sorting runs - initial render can look fine
      // by coincidence (natural row order, or no fallback rows on that page) while an actual
      // sort exercises every pair and hits the NaN case.
      valueGetter: (value, row) => {
        const latest = resolveLatestCallDate(row);
        if (latest) return latest;
        return value ? new Date(value) : null;
      },
      renderCell: (params) => {
        const latestCallDate = resolveLatestCallDate(params.row) || params.value;
        return (
          <span className="inbound-table__mono-value">
            {latestCallDate ? formatInPracticeTimezone(latestCallDate, practiceTimezone) : ""}
          </span>
        );
      },
    },
    {
      field: "CallDetails",
      headerName: "Call Details",
      minWidth: 120,
      flex: 0.95,
      sortable: false,
      renderCell: (params) => (
        <button
          type="button"
          className="inbound-table__text-button"
          onClick={() => setCallsDialogRow(params.row)}
        >
          View Details
        </button>
      ),
    },
    {
      field: "PatientContactNumber",
      headerName: "Client",
      minWidth: 150,
      flex: 1,
      renderCell: (params) => (
        <span className="inbound-table__mono-value">
          {params.value || "-"}
        </span>
      ),
    },
    {
      field: "Actions",
      headerName: "Actions",
      minWidth: 110,
      flex: 0.7,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div className="inbound-table__actions">
          <button
            type="button"
            className="inbound-table__menu-trigger"
            onClick={(event) => handleOpenActionMenu(event, params.row)}
            aria-label="Open actions menu"
          >
            <MoreVertical size={18} strokeWidth={2} />
          </button>
        </div>
      ),
    },
  ].map((column) => ({
    align: "left",
    headerAlign: "left",
    ...column,
  }));

  const handleCloseModal = () => {
    setModalShow(false);
    setTranscriptRecord("");
    setTransferHistory([]);
    setFollowUpReasonHistory([]);
  }

  const menuHandle = (id) => {
    setMenuId(id)
    setActiveModal("menu");
    transcriptionsFetchHandle(id, PracticeId);
  }

  const summaryHandle = (id) => {
    setActiveModal("summary");
    setMenuId(id)
    transcriptionsFetchHandle(id, PracticeId);
  }

  const statusHandle = (id) => {
    setActiveModal("status");
    setMenuId(id)
    transcriptionsFetchHandle(id, PracticeId);
  }

  // True server-side pagination, matching Summary tab exactly, EXCEPT when a client-only
  // filter (range/transfer/duration/follow-up) is active - none of those can be expressed as a
  // server query param, so in that case this instead fetches every row matching the date
  // filter, and totalRows/pageCount are based on the client-filtered count (see
  // hasActiveClientFilter/filteredInboundRows above) rather than the raw server total. This is
  // what keeps the "Showing X-Y of Z" footer accurate when a filter is engaged - basing it on
  // the raw per-page fetch's own total would silently ignore however many rows the filter
  // actually removed.
  async function fetchInboundPage() {
    if (!PracticeId || !token) return;

    // Claim this fetch as the latest one. If a newer fetch starts before this one resolves,
    // its result must not be allowed to overwrite state with stale data.
    const seq = ++inboundFetchSeqRef.current;

    if (userType === "admin") {
      setInBoundsCalls([]);
    }
    setIsData(true);

    const url = activeDateFilter ? `/api/SearchWithDatePAuthQ` : `/api/PriorAuthQLead`;
    const baseParams = activeDateFilter
      ? {
          PracticeId,
          startDate: activeDateFilter.startDate,
          endDate: activeDateFilter.endDate,
          practiceContactNumber: inboundContact || practiceContactNumber,
          inboundContact,
        }
      : {
          PracticeId,
          practiceContactNumber: inboundContact || practiceContactNumber,
        };
    const params = hasActiveClientFilter
      ? baseParams
      : {
          ...baseParams,
          limit: paginationModel.pageSize,
          offset: paginationModel.page * paginationModel.pageSize,
        };

    const result = await paginatedRequest(token, url, params);

    if (seq !== inboundFetchSeqRef.current) {
      // Superseded by a newer fetch while this one was in flight - drop this result entirely,
      // including the loading-flag cleanup, which the newer fetch already owns.
      return;
    }

    if (result.error === "Token is expired, login again") {
      setIsTokenExpired(true);
      setIsData(false);
      return;
    }

    if (result.practiceTimezone) {
      setPracticeTimezone(result.practiceTimezone);
    }

    if (result.fixedTimezone) {
      setFixedTimezone(result.fixedTimezone);
    }

    const incoming = enrichInboundCallRows(result.rows || []);
    setInBoundsCalls(incoming);
    setTotalRows(
      hasActiveClientFilter ? applyInboundRowFilters(incoming, filters).length : result.total,
    );
    setIsCallsDataGet(true);
    setIsData(false);
  }

  // Forces a re-fetch of whatever page is currently showing, resetting to page 1 first - used
  // after an action that changes the underlying list.
  const refetchFromFirstPage = () => {
    setPaginationModel((current) => (current.page === 0 ? current : { ...current, page: 0 }));
    setRefetchNonce((n) => n + 1);
  };

  async function transcriptionsFetchHandle(menuId, PracticeId) {
    setTranscriptRecord("");

    let url = "/api/getInOutboundCallsTranscripts";
    let obj = {
      leadId: menuId,
      memberListType: "InBoundCalls",
      PracticeId: PracticeId
    }
    const userData = await dateRangeRequest(token, url, obj);
    if (userData == "Token is expired, login again") {
      setIsTokenExpired(true)
    }
    else if (userData == "no record") {
      // setTranscriptsDetail([]);
      setModalShow(true)
    }
    else {
      // setTranscriptsDetail(userData);
      let transcriptionsText = "";
      let summariesText = "";
      let statusesText = "";
      // Only the calls inside the selected date range - this list used to always show every
      // call the patient ever made, even when the grid itself was already filtered down to a
      // date range, which made "View Status" disagree with the row that opened it. One record
      // per real transcript here (not per raw call attempt like the grid's Calls[]), so this
      // list is always small - no need for the same per-page memoization as the Date & Time
      // column above.
      const rawRecords = Array.isArray(userData) ? userData : [];
      const inRangeRecords = activeDateFilter
        ? (() => {
            const dayFormatter = new Intl.DateTimeFormat("en-CA", {
              timeZone: practiceTimezone,
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            });
            return rawRecords.filter((record) => {
              const entryDate = record?.CreateDateTime ? new Date(record.CreateDateTime) : null;
              if (!entryDate || Number.isNaN(entryDate.getTime())) return false;
              const dayKey = dayFormatter.format(entryDate);
              return dayKey >= activeDateFilter.startDate && dayKey <= activeDateFilter.endDate;
            });
          })()
        : rawRecords;
      const sortedRecords = [...inRangeRecords].sort(
        (left, right) =>
          new Date(right?.CreateDateTime || 0).getTime() -
          new Date(left?.CreateDateTime || 0).getTime(),
      );
      sortedRecords.length > 0 ? sortedRecords.map((record) => {
        const formattedDate = new Date(record.CreateDateTime).toLocaleDateString("en-US", {
          timeZone: practiceTimezone,
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })

        transcriptionsText +=
          `<b>${formattedDate}</b>
<pre>${record.TranscriptText}</pre>
\n`
        summariesText +=
          `<b>${formattedDate}</b>
<pre>${record.Summary}</pre>
\n`
        const durationSec = Number(record.DurationSec || 0);
        const durationMin = Math.floor(durationSec / 60);
        const durationRemSec = durationSec % 60;
        const durationLabel = durationSec > 0
          ? ` &nbsp;|&nbsp; ${durationMin}:${String(durationRemSec).padStart(2, "0")} min`
          : "";
        statusesText +=
          `<b>${formattedDate}</b>${durationLabel}
<pre>${record.Status}</pre>
\n`
      }) : "";

      if (transcriptionsText) {
        setTranscriptRecord(transcriptionsText);
        setModalShow(true)
      }
      else {
        setTranscriptRecord("");
      }

      if (summariesText) {
        setSummary(summariesText);
        setModalShow(true)
      }
      else {
        setSummary("");
      }

      if (statusesText) {
        setStatus(statusesText);
        setModalShow(true)
      }
      else {
        setStatus("");
      }
    }
  }

  const allowCallHandle = async (e) => {
    doNotCall = false;
    let id = userId;
    let obj = { doNotCall, id };

    let url = `/api/PriorAuthQLeadDoNotCall`;
    const userData = await usersPostRequest(token, url, obj);
    if (userData == "Token is expired, login again") {
      setIsTokenExpired(true)
    } else {
      // priorAuthQFetchHandle()
      setModalShow(false)
    }
  }

  // debugger
  // const getRowId = (row) => row.Id;
  const getRowId = (row) => row?.Id ?? uuidv4();

  async function handleSearchInputChange() {
    let url = `/api/SearchPriorAuthQLead`;
    let obj = { patientName, providerName, PracticeId }
    const userData = await usersPostRequest(token, url, obj);
    if (userData == "Token is expired, login again") {
      setIsTokenExpired(true)
    }
    else {
      const incoming = enrichInboundCallRows(userData == "no record" ? [] : userData);
      setInBoundsCalls(incoming);
      setTotalRows(incoming.length);
      setIsCallsDataGet(true);
      setPaginationModel((current) => (current.page === 0 ? current : { ...current, page: 0 }));
    }
  }

  async function handleStartDateChange() {
    let url = "/api/SearchWithDatePAuthQ";
    let obj = {
      startDate: startDate,
      endDate: endDate,
      PracticeId: PracticeId
    }
    const userData = await dateRangeRequest(token, url, obj);
    if (userData == "Token is expired, login again") {
      setIsTokenExpired(true)
    }
    else {
      setPriorAuthQGridData(userData);
    }
  }

  const filterTranscriptRecord = () => {
    // if (priorAuthQGridData.length > 0 && menuId != null) {
    let filteredData = priorAuthQGridData.filter((e) => e.Id === menuId);
    setTranscriptRecord(filteredData);
    // }
  }

  // useEffect(() => {
  //     if (menuId != null) {
  //         filterTranscriptRecord()
  //     }
  // }, [menuId])

  // Name search (providerName/patientName, driven from Home.jsx's search box) hits its own
  // separate, non-paginated endpoint that returns every match at once - kept in its own effect,
  // keyed only on the search terms, exactly as before.
  useEffect(() => {
    if (providerName || patientName) {
      setSideBarLinkActive("careConsole");
      handleSearchInputChange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerName, patientName])

  // Page/page-size only need a fresh fetch when the server itself owns pagination
  // (paginatedRequest sends limit/offset - see fetchInboundPage). Once a client-side filter is
  // active, every row matching the date filter is already loaded, and filteredInboundRows above
  // slices out the current page from that for free - re-fetching on a page change was not just
  // wasted work, it actively broke Next/Previous: fetchInboundPage briefly clears inBoundsCalls
  // to [] while the refetch is in flight, the totalRows-sync effect below reacts to that empty
  // array by setting totalRows to 0, and the clamp effect further down then saw "page > 0 pages
  // exist" and snapped straight back to page 0 before the real (unchanged) data even finished
  // reloading - the page number could never advance while a filter was on.
  const pageFetchKey = hasActiveClientFilter
    ? "all"
    : `${paginationModel.page}:${paginationModel.pageSize}`;

  // The main paginated fetch - runs whenever the practice, the active date filter, refetchNonce,
  // hasActiveClientFilter (switching between "one server page" and "everything matching the date
  // filter"), or (only in server-pagination mode) the current page/page size changes. Skipped
  // while a name search is active (the effect above owns that case instead), same as before.
  // Does NOT re-run on every individual filter value tweak while already in "everything" mode -
  // see the totalRows-sync effect below for why that doesn't need a fresh fetch.
  useEffect(() => {
    if (!PracticeId || !token) return;
    if (providerName || patientName) return;
    fetchInboundPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    PracticeId,
    token,
    providerName,
    patientName,
    pageFetchKey,
    activeDateFilter,
    refetchNonce,
    hasActiveClientFilter,
    timezonePreferenceVersion,
  ])

  // Keeps totalRows accurate while already in "everything matching the date filter" mode, as
  // the OTHER (non-date) filter values change - those don't require a fresh fetch (the full set
  // is already loaded), just a re-count against the new filter values.
  useEffect(() => {
    if (!hasActiveClientFilter) return;
    setTotalRows(applyInboundRowFilters(inBoundsCalls, filters).length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActiveClientFilter, inBoundsCalls, filters])

  // A bulk upload completing (isUploadLead) should show the freshly uploaded leads from the top.
  useEffect(() => {
    if (isUploadLead) {
      setIsUploadLead(false);
      refetchFromFirstPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUploadLead])

  // Filter button (Home.js) bumps filterNonce - switch to the date-filtered, paginated endpoint
  // using whatever startDate/endDate are currently set (or back to the unfiltered one if
  // cleared), landing back on page 1 of the new filter. The actual fetch happens via the main
  // pagination effect above reacting to activeDateFilter/page changing.
  useEffect(() => {
    if (filterNonce) {
      // Empty startDate/endDate (filter cleared) must fall back to the unfiltered
      // endpoint - {startDate: "", endDate: ""} is still a truthy object, so it would
      // otherwise keep hitting SearchWithDatePAuthQ with blank date params instead of
      // reverting to PriorAuthQLead.
      const hasDateFilter = Boolean(startDate) && Boolean(endDate);
      setActiveDateFilter(hasDateFilter ? { startDate, endDate } : null);
      setPaginationModel((current) => ({ ...current, page: 0 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterNonce])

  // Token-expiry redirect is now handled once, centrally, in ContextApi.js - see its comment
  // for why this component's own copy of the same effect was removed.

  // Clamp down if the current page no longer exists (e.g. pageSize grew, or the server-side
  // total shrank) - based on totalRows (the server's own count), not the client-filtered rows,
  // since a client-only filter narrowing the CURRENT page's visible rows doesn't mean there are
  // fewer real pages of data on the server.
  useEffect(() => {
    const nextPageCount = Math.max(1, Math.ceil(totalRows / paginationModel.pageSize));

    if (paginationModel.page > nextPageCount - 1) {
      setPaginationModel((current) => ({
        ...current,
        page: Math.max(0, nextPageCount - 1),
      }));
    }
  }, [totalRows, paginationModel.page, paginationModel.pageSize]);

  //Add Export Button Functionality

  const apiRef = useGridApiRef();
  const getVisibleRowsToExport = () =>
    gridFilteredSortedRowIdsSelector(apiRef).filter((id) => {
      const rowNode = gridRowTreeSelector(apiRef)[id];

      return rowNode && rowNode.type !== "footer";
    });

  // Shared by both download buttons - the date filter and transfer filter above only decide
  // which LEAD qualifies (a lead with even one matching call passes), so buildMultiRowCsv needs
  // these to also check each of that lead's calls on its own before turning it into a CSV row.
  const buildCsvExportFilters = () => ({
    dateRange: activeDateFilter
      ? {
          startDate: activeDateFilter.startDate,
          endDate: activeDateFilter.endDate,
          dayFormatter: new Intl.DateTimeFormat("en-CA", {
            timeZone: practiceTimezone,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          }),
        }
      : null,
    transferFilter: filters.transfer,
    timeZone: practiceTimezone,
  });

  const handleExport = () => {
    const visibleRows = getVisibleRowsToExport()
      .map((id) => apiRef.current.getRow(id))
      .filter(Boolean);
    const csvContent = buildMultiRowCsv(visibleRows, buildCsvExportFilters());

    downloadCsvFile(
      csvContent,
      `InBoundCallsData_${moment().format("YYYYMMDD_HHmmss")}.csv`,
    );
  };

  // Download All - fetches every row matching the current date filter fresh from the backend
  // (omitting limit/offset puts /api/PriorAuthQLead and /api/SearchWithDatePAuthQ into their
  // "send everything" mode, same convention Summary tab's export already relies on) instead of
  // exporting only whatever page(s) are currently loaded in inBoundsCalls. The other, client-only
  // filters (range/transfer/duration/follow-up) still get applied afterward via
  // applyInboundRowFilters, so the export matches what's currently filtered on screen, just
  // without the pagination cap.
  const handleExportAll = async () => {
    const effectiveFilter = activeDateFilter;
    const url = effectiveFilter ? "/api/SearchWithDatePAuthQ" : "/api/PriorAuthQLead";
    const params = effectiveFilter
      ? {
          PracticeId,
          startDate: effectiveFilter.startDate,
          endDate: effectiveFilter.endDate,
          practiceContactNumber: inboundContact || practiceContactNumber,
          inboundContact,
        }
      : {
          PracticeId,
          practiceContactNumber: inboundContact || practiceContactNumber,
        };

    const result = await paginatedRequest(token, url, params);
    const enriched = enrichInboundCallRows(result.rows || []);
    const allRows = applyInboundRowFilters(enriched, filters);
    const csvContent = buildMultiRowCsv(allRows, buildCsvExportFilters());

    downloadCsvFile(
      csvContent,
      `InBoundCallsData_${moment().format("YYYYMMDD_HHmmss")}.csv`,
    );
  };

  const handleOpenPromptHub = () => {
    setSideBarLinkActive("onBoardingForm");
    navigate("/prompt-hub/inbound");
  };

  const handleFilterChange = (name, value) => {
    setFilters((current) => ({
      ...current,
      [name]: value,
    }));
    setPaginationModel((current) => (current.page === 0 ? current : { ...current, page: 0 }));
  };

  const handleResetFilters = () => {
    setFilters(INBOUND_FILTER_DEFAULTS);
    setPaginationModel((current) => (current.page === 0 ? current : { ...current, page: 0 }));
  };

  // Matches Summary tab's own footer/pagination exactly - true server pagination, so this is
  // "which raw rows of the true total are on this page," not a "loaded so far" count.
  const pageCount = Math.max(1, Math.ceil(totalRows / paginationModel.pageSize));
  const currentPage = Math.min(paginationModel.page, pageCount - 1);
  const canGoPreviousPage = currentPage > 0;
  const canGoNextPage = currentPage < pageCount - 1;
  const rangeStart = totalRows > 0 ? currentPage * paginationModel.pageSize + 1 : 0;
  const rangeEnd = Math.min((currentPage + 1) * paginationModel.pageSize, totalRows);

  return (
    <>
      <div className="inbound-call-table">
        <div className="inbound-call-table-topbar">
          <div className="inbound-call-table-topbar-controls">
            {tableTopBarControls}
            <button
              type="button"
              className="home-date-range-picker__trigger"
              onClick={() => setIsFilterDialogOpen(true)}
              aria-label="Open inbound filters"
            >
              <span className="home-date-range-picker__trigger-icon">
                <Filter size={16} strokeWidth={2.1} />
              </span>
              <span className="home-date-range-picker__trigger-text">Filter By</span>
            </button>
          </div>
          <div className="inbound-call-table-topbar-actions">
            <PageActionMenu
              onPromptHub={handleOpenPromptHub}
              onDownload={handleExportAll}
              onDownloadCurrentPage={handleExport}
              downloadLabel="Download All (CSV)"
              downloadCurrentPageLabel="Download Current Page (CSV)"
            />
          </div>
        </div>
        {activeDateFilter ? (
          <div className="call-activity-range-note">
            Showing leads with call activity in this range
            {hasActiveClientFilter
              ? " - and matching your other filters (Range/Transfer/Duration/Follow-up). If nothing shows, try clearing one of them."
              : ""}
          </div>
        ) : null}
        <DataGrid
          apiRef={apiRef}
          rows={filteredInboundRows}
          rowHeight={48}
          columnHeaderHeight={48}
          scrollbarSize={8}
          columns={columns}
          pagination
          paginationMode="server"
          rowCount={totalRows}
          hideFooter
          autoWidth
          initialState={{ sorting: { sortModel: [{ field: "CreateDateTime", sort: "desc" }] } }}
          pageSizeOptions={INBOUND_PAGE_SIZE_OPTIONS}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          // Re-sorting changes which rows land on the current page number, but nothing else
          // here resets pagination on a sort change (the effect above only clamps the page
          // when the page COUNT shrinks) - so a row could silently move to a different page
          // while you stay put, looking like it vanished. Always land back on page 1 of the
          // new order instead.
          onSortModelChange={() => setPaginationModel((current) => ({ ...current, page: 0 }))}
          loading={userType === "admin" && isData === true}
          getRowId={getRowId}
          disableRowSelectionOnClick={true}
          rowSelection={false}
          disableColumnMenu
          // IsPendingCall (set server-side only when a date range is active - see
          // /api/SearchWithDatePAuthQ) marks a lead that has never been called at all but is
          // still shown because it was created within the selected range - dim it so it reads
          // as "uploaded, not called yet" instead of looking like unexplained call activity.
          getRowClassName={(params) => (params.row?.IsPendingCall ? "inbound-table__row--pending" : "")}
          sx={{
            border: 0,
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell": {
              justifyContent: "flex-start",
              alignItems: "center",
              px: 1.75,
            },
            "& .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-cellContent": {
              width: "100%",
              justifyContent: "flex-start",
              textAlign: "left",
            },
            "& .MuiDataGrid-columnHeaderTitleContainerContent": {
              width: "100%",
              justifyContent: "flex-start",
            },
            "& .MuiDataGrid-cell": {
              py: 0,
            },
            // Restored: the earlier plain global App.css rule for this had lower CSS
            // specificity than MUI's own generated default-focus-outline rule, so it lost the
            // cascade and the outline came back. The sx prop's own generated wrapper class
            // gives this selector one more class of specificity than a plain global rule,
            // which is why this scoped version reliably wins.
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within":
              {
                outline: "none",
              },
          }}
        />
        <CallFilterDialog
          open={isFilterDialogOpen}
          title="Inbound Filters"
          description="Narrow inbound results using the same workspace controls."
          sections={INBOUND_FILTER_SECTIONS}
          values={filters}
          onChange={handleFilterChange}
          onClose={() => setIsFilterDialogOpen(false)}
          onApply={() => setIsFilterDialogOpen(false)}
          onReset={handleResetFilters}
        />
        <div className="inbound-call-table-footer">
          <div className="inbound-call-table-footer-primary">
            <div className="inbound-call-table-count">
              {totalRows > 0 ? `Showing ${rangeStart}-${rangeEnd} of ${totalRows}` : ""}
            </div>
          </div>
          <div className="inbound-call-table-footer-controls">
            <div className="inbound-call-table-page-size">
              <span className="inbound-call-table-page-size-icon" aria-hidden="true">
                <List size={16} strokeWidth={2} />
              </span>
              <label
                htmlFor="inbound-call-table-page-size"
                className="inbound-call-table-sr-only"
              >
                Rows per page
              </label>
              <div className="inbound-call-table-page-size-select-wrap">
                <select
                  id="inbound-call-table-page-size"
                  className="inbound-call-table-page-size-select"
                  value={paginationModel.pageSize}
                  onChange={(event) => {
                    const nextPageSize = Number(event.target.value);
                    setPaginationModel({
                      page: 0,
                      pageSize: nextPageSize,
                    });
                  }}
                >
                  {INBOUND_PAGE_SIZE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <span
                  className="inbound-call-table-page-size-chevron"
                  aria-hidden="true"
                >
                  <ChevronDown size={16} strokeWidth={2} />
                </span>
              </div>
            </div>
            <span className="inbound-call-table-footer-divider" aria-hidden="true" />
            <div className="inbound-call-table-pagination">
              <button
                type="button"
                onClick={() =>
                  setPaginationModel((current) => ({
                    ...current,
                    page: Math.max(current.page - 1, 0),
                  }))
                }
                disabled={!canGoPreviousPage}
                className="inbound-call-table-nav-button"
                aria-label="Previous page"
              >
                <ChevronLeft size={16} strokeWidth={2.1} />
              </button>
              <div className="inbound-call-table-page-indicator">
                {currentPage + 1} / {pageCount}
              </div>
              <button
                type="button"
                onClick={() =>
                  setPaginationModel((current) => ({
                    ...current,
                    page: Math.min(current.page + 1, pageCount - 1),
                  }))
                }
                disabled={!canGoNextPage}
                className="inbound-call-table-nav-button"
                aria-label="Next page"
              >
                <ChevronRight size={16} strokeWidth={2.1} />
              </button>
            </div>
          </div>
        </div>
        <Menu
          anchorEl={actionMenuAnchor}
          open={actionMenuOpen}
          onClose={handleCloseActionMenu}
          PaperProps={{ className: "inbound-table__menu" }}
          MenuListProps={{ className: "inbound-table__menu-list" }}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <MenuItem
            className="inbound-table__menu-item"
            disabled={buildTransferHistory(actionMenuRow).length === 0}
            onClick={() => {
              if (!actionMenuRow) return;
              handleCloseActionMenu();
              openTransferModal(actionMenuRow);
            }}
          >
            View Transfer
          </MenuItem>
          <MenuItem
            className="inbound-table__menu-item"
            disabled={buildFollowUpReasonHistory(actionMenuRow).length === 0}
            onClick={() => {
              if (!actionMenuRow) return;
              handleCloseActionMenu();
              openFollowUpReasonModal(actionMenuRow);
            }}
          >
            View Reason
          </MenuItem>
          <MenuItem
            className="inbound-table__menu-item"
            onClick={() => {
              if (!actionMenuRow) return;
              handleCloseActionMenu();
              menuHandle(actionMenuRow.Id);
            }}
          >
            View Transcript
          </MenuItem>
          <MenuItem
            className="inbound-table__menu-item"
            onClick={() => {
              if (!actionMenuRow) return;
              handleCloseActionMenu();
              summaryHandle(actionMenuRow.Id);
            }}
          >
            View Summary
          </MenuItem>
        </Menu>
      </div>
      <Modal
        className="popup"
        show={modalShow}
        onHide={handleCloseModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {
              activeModal === "menu"
                ? "Transcripts"
                : activeModal === "summary"
                  ? "Summary"
                  : activeModal === "transfer"
                    ? "Transfer"
                    : activeModal === "followUpReason"
                      ? "Follow-Up Reason"
                  : "Status"
              // : activeModal === "Delete" ? "Delete Record"
              //     : "Queue Call"
            }
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {activeModal === "menu" ? (
            <p>
              {" "}
              {transcriptRecord ? (
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(transcriptRecord) }} />
              ) : (
                // transcriptsDetail.map((record) => {
                //   <>
                //     <b>{record.CreatedDateTime}</b>
                //     <pre>{record.TranscriptText}</pre>
                //     <hr/>
                //   </>
                // })
                ""
              )}{" "}
            </p>
          ) : activeModal === "summary" ? (
            <p>
              {" "}
              {transcriptRecord ? (
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(summary) }} />
              ) : (
                ""
              )}{" "}
            </p>
          ) : activeModal === "status" ? (
            <p>
              {" "}
              {transcriptRecord ? (
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(status) }} />
              ) : (
                ""
              )}{" "}
            </p>
          ) : activeModal === "transfer" ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {/* BEGIN: Task 1 - Transfer modal history display
                  REVERT RULE ("revert ui changes"):
                  remove the bold/stacked transfer formatting and restore the previous layout. */}
              {transferHistory.length > 0 ? (
                transferHistory.map((item) => {
                  const transferMoment = item?.startedAt ? moment(item.startedAt) : null;

                  return (
                    <div
                      key={`${item?.transferredNumber}-${item?.startedAt || "transfer"}`}
                      style={{ display: "flex", flexDirection: "column", gap: "4px" }}
                    >
                      <div style={{ fontWeight: 700 }}>
                        {transferMoment?.isValid()
                          ? transferMoment.format("MMMM DD, YYYY [at] hh:mm:ss A")
                          : "-"}{" "}
                        | {formatTransferDuration(item?.durationSec)}
                      </div>
                      <div>{item?.transferredNumber || "-"}</div>
                    </div>
                  );
                })
              ) : (
                <p>-</p>
              )}
            </div>
          ) : activeModal === "followUpReason" ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {followUpReasonHistory.length > 0 ? (
                followUpReasonHistory.map((item, index) => {
                  const followUpMoment = item?.startedAt ? moment(item.startedAt) : null;

                  return (
                    <div
                      key={`${item?.reason}-${item?.startedAt || index}`}
                      style={{ display: "flex", flexDirection: "column", gap: "4px" }}
                    >
                      <div style={{ fontWeight: 700 }}>
                        {followUpMoment?.isValid()
                          ? followUpMoment.format("MMMM DD, YYYY [at] hh:mm:ss A")
                          : "-"}{" "}
                        | Follow-Up
                      </div>
                      <div>{item?.reason || "-"}</div>
                    </div>
                  );
                })
              ) : (
                <p>-</p>
              )}
            </div>
          ) : activeModal == "Delete" && transcriptRecord ? (
            <p>You can't delete this lead as it has been already processed</p>
          ) : activeModal == "Delete" ? (
            <p>
              Are you sure that you don't want to process call on this lead
            </p>
          ) : (
            <p>
              Are you sure that you want to requeue call on this lead again
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          {
              activeModal === "menu" ||
              activeModal === "summary" ||
              activeModal === "status" ||
              activeModal === "transfer" ||
              activeModal === "followUpReason" ? (
              <button className="simple-btn" onClick={handleCloseModal}>
                Close
              </button>
            ) : (
              ""
            )
            // activeModal === "Delete" && transcriptRecord ?
            //     <button className="simple-btn me-2" id="closeTrue" onClick={closeCallHandle}>Close</button>
            //     :
            //     activeModal === "Delete" ?
            //         <>
            //             <button className="simple-btn me-2" id="closeTrue" onClick={closeCallHandle}>No</button>
            //             <button className="simple-btn" id="updateTrue" onClick={cancelCallHandle}>Yes</button>
            //         </>
            //         :
            //         <>
            //             <button className="simple-btn me-2" id="closeTrue" onClick={closeCallHandle}>No</button>
            //             <button className="simple-btn" id="updateTrue" onClick={queueCallHandle}>Yes</button>
            //         </>
          }
        </Modal.Footer>
      </Modal>

      <CallsBreakdownDialog
        open={callsDialogRow !== null}
        onClose={() => setCallsDialogRow(null)}
        filter="all"
        groupByConnection
        ambiguousNote={
          callsDialogRow?.CallHistoryMayBeShared
            ? "Another lead with this same phone number was uploaded at the same time - some of this call history may actually belong to that lead instead."
            : ""
        }
        calls={callsDialogFilteredCalls}
        leads={[]}
        totals={{
          // "Total Calls" needs to match the filtered list above, or the header would show a
          // stale all-time count while the table below only lists the in-range calls.
          total: activeDateFilter ? callsDialogFilteredCalls.length : (callsDialogRow?.TotalCallAttempts ?? 0),
          completed: callsDialogRow?.CallReceived ?? 0,
          durationSec: callsDialogRow?.TotalDurationSec ?? 0,
          billableDurationSec:
            callsDialogRow?.TotalAllLegsDurationSec ?? callsDialogRow?.TotalDurationSec ?? 0,
          transferLegs: { calls: 0, durationSec: 0 },
        }}
        timeZone={practiceTimezone}
        fixedTimezone={fixedTimezone}
      />
    </>
  );
};
